import { Link } from "react-router-dom";
import { Code2, Cloud, Server, Workflow, ShieldCheck, LifeBuoy, Check, BrainCircuit } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { CtaBanner } from "@/components/CtaBanner";
import { AiStack } from "@/components/AiStack";
import { AiWalkthrough } from "@/components/AiWalkthrough";
import { SERVICES } from "@/config/site";

const ICONS = {
  "application-development": Code2,
  "ai-applications": BrainCircuit,
  "cloud-infrastructure": Cloud,
  "server-deployment": Server,
  "devops-automation": Workflow,
  "security-reliability": ShieldCheck,
  "ongoing-support": LifeBuoy,
};

export default function ServicesPage() {
  return (
    <>
      <Seo
        title="App Development, AI, Cloud & DevOps Services | Tedmo Services"
        description="Custom application development, AI applications and capabilities, cloud infrastructure on AWS, Azure and Google Cloud, server deployment, DevOps automation, security, and ongoing support."
        path="/services"
      />

      <section className="py-20 border-b border-line" data-testid="services-hero">
        <div className="max-w-6xl mx-auto px-6">
          <span className="font-mono-sub text-xs text-royal uppercase tracking-wider font-bold">
            What We Do
          </span>
          <h1
            data-testid="services-page-title"
            className="font-poster text-4xl sm:text-5xl font-extrabold text-steel mt-2 max-w-3xl leading-tight"
          >
            Services, in plain language
          </h1>
          <p className="text-lg text-ink mt-6 max-w-2xl leading-relaxed">
            Every service below exists to solve a business problem — not to sell
            you technology. Here is what each one means for your company.
          </p>
        </div>
      </section>

      <section className="py-16 max-w-6xl mx-auto px-6 space-y-16" data-testid="services-list">
        {SERVICES.map((service, i) => {
          const Icon = ICONS[service.slug];
          const flip = i % 2 === 1;
          return (
            <Reveal key={service.slug}>
              <article
                id={service.slug}
                data-testid={`service-detail-${service.slug}`}
                className={`grid grid-cols-1 md:grid-cols-5 gap-8 items-center scroll-mt-28 ${
                  flip ? "md:[&>*:first-child]:order-2" : ""
                }`}
              >
                <div className="md:col-span-2">
                  <div className="group relative h-56 overflow-hidden rounded-2xl border border-line shadow-sm">
                    <img
                      src={service.image}
                      alt={service.imageAlt}
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-steel/90 via-steel/20 to-transparent" />
                    <span className="absolute left-4 top-4 flex h-11 w-11 items-center justify-center rounded-xl border border-white/20 bg-white/10 text-white backdrop-blur">
                      <Icon className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <div className="absolute inset-x-0 bottom-0 flex items-center justify-between p-4">
                      <span className="font-mono-sub text-[11px] font-semibold uppercase tracking-wider text-white/90">
                        {service.tag}
                      </span>
                      <span className="font-mono-sub text-[11px] font-semibold tracking-widest text-royal-bright">
                        0{i + 1}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="md:col-span-3">
                  <span className="font-mono-sub text-[11px] text-royal font-semibold uppercase tracking-wider">
                    {service.tag}
                  </span>
                  <h2 className="font-poster text-2xl font-bold text-steel mt-1">
                    {service.name}
                  </h2>
                  <p className="text-sm font-semibold text-steel mt-4">
                    The problem:{" "}
                    <span className="font-normal text-ink">{service.problem}</span>
                  </p>
                  <p className="text-sm text-ink mt-3 leading-relaxed">{service.detail}</p>
                  <ul className="mt-4 space-y-1.5">
                    {service.outcomes.map((outcome) => (
                      <li key={outcome} className="flex items-center gap-2 text-xs font-mono-sub text-mute">
                        <Check className="w-3.5 h-3.5 text-royal" aria-hidden="true" />
                        {outcome}
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            </Reveal>
          );
        })}
      </section>

      <AiStack />

      <AiWalkthrough />

      <section className="pb-20 max-w-6xl mx-auto px-6">
        <Reveal>
          <div className="rounded-xl border border-line bg-white p-8 flex flex-col sm:flex-row items-center justify-between gap-6">
            <p className="text-sm text-ink max-w-xl">
              Not sure which service fits? Describe your project and we will
              point you in the right direction — no obligation.
            </p>
            <Link
              to="/contact"
              data-testid="services-cta-button"
              className="shrink-0 px-8 py-3.5 rounded bg-royal hover:bg-royal-dark text-white text-sm font-semibold transition-colors"
            >
              Request a Consultation
            </Link>
          </div>
        </Reveal>
      </section>

      <CtaBanner title="One team, from first sketch to ongoing support" />
    </>
  );
}
